import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import PostCard from './PostCard';
import { MainButton } from 'global-styles/MainButton';
import { generateCssStyle } from 'styles/breakPoints';
import { usePostList } from 'react-query/blogHooks';
import { useBlogContext } from 'context/BlogContext';
import { GlassLoader } from 'components/Common/GlassLoader';
import NoPost from './NoPost';
import { useRouter } from 'next/router';

const PostListSection = () => {
  const { locale } = useRouter();
  const {
    data: postListRes,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePostList();
  const {
    state: { prevScrollTop },
    dispatch,
  } = useBlogContext();
  const noPosts = useMemo(() => {
    if (isLoading) return false;
    const flatPosts = postListRes.pages
      .map(page => page.posts)
      .flat()
      .flat();

    return flatPosts.length === 0;
  }, [postListRes, isLoading]);

  // 페이지로 돌아올 경우 스크롤 위치 회복
  useEffect(() => {
    if (prevScrollTop) window.scrollTo({ top: prevScrollTop });

    dispatch({ type: 'change', newState: { prevScrollTop: 0 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading)
    return (
      <PostListSectionContainer>
        <GlassLoader loadingText="Loading Posts..." />
      </PostListSectionContainer>
    );

  return (
    <PostListSectionContainer>
      {noPosts ? (
        <NoPost
          text={{
            en: 'Matched result not found.',
            ko: '검색 결과에 해당하는 글이 없습니다.',
          }}
        />
      ) : (
        <ul className="card-list">
          {postListRes?.pages.map(page => {
            return page.posts.map(post => (
              <PostCard
                key={post.id}
                title={post.title}
                category={post.category}
                publishedDate={post.date.date}
                thumbnail={post.thumbnail || ''}
                postId={post.id}
                forPostUI={false}
              />
            ));
          })}
        </ul>
      )}

      {!noPosts && (
        <MainButton
          className="p-b-btn"
          buttonType={
            isFetchingNextPage || !hasNextPage ? 'disabled' : 'default'
          }
          onClick={() => fetchNextPage()}
          text={
            locale === 'en'
              ? isFetchingNextPage
                ? 'Loading...'
                : hasNextPage
                ? 'Load more'
                : 'You are on last page.'
              : isFetchingNextPage
              ? '로드중...'
              : hasNextPage
              ? '더 보기'
              : '마지막 페이지 입니다.'
          }
          injectInlineStyles={{ height: '48px', marginTop: '56px' }}
          injectCssStyles={[
            generateCssStyle({
              width: hasNextPage ? '156px' : 'fit-content',
              padding: hasNextPage ? '0' : '0 32px',
            }),
          ]}
        />
      )}
    </PostListSectionContainer>
  );
};

export default PostListSection;

const PostListSectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  .card-list {
    width: 100%;
    display: grid;
    grid-gap: 24px;
    grid-template-columns: repeat(3, minmax(200px, 1fr));
  }

  @media screen and (max-width: 1239px) {
    .card-list {
      grid-gap: 16px;
      grid-template-columns: repeat(2, minmax(200px, 1fr));
    }
  }

  @media screen and (max-width: 599px) {
    .card-list {
      grid-template-columns: repeat(1, minmax(200px, 1fr));
    }
  }
`;
